import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LanguagePreferencesFields } from "@/components/profile/LanguagePreferencesFields";
import { useApp } from "@/hooks/use-app-context";
import { Icon } from "@/lib/icons";
import { getGenders } from "@/lib/signup.constants";
import { CountryPhoneInput } from "@/components/auth/CountryPhoneInput";
import { CountrySelect } from "@/components/ui/country-select";
import type { ProfileForm } from "./profile-form";
import type { SettingsCopy } from "./settings-copy";
import { SettingsSheet, SheetField } from "./SettingsSheet";
import type { SettingsState } from "./use-settings";

interface EditSheetProps {
  state: SettingsState;
  copy: SettingsCopy;
}

function SaveFooter({ state }: { state: SettingsState }) {
  const { t } = useApp();
  return (
    <>
      <Button variant="ghost" onClick={state.closeSheet} disabled={state.isSaving}>
        {t.cancel}
      </Button>
      <Button onClick={() => void state.save()} disabled={state.isSaving}>
        {state.isSaving ? <Icon name="loader2" className="animate-spin" /> : <Icon name="save" />}
        {t.save}
      </Button>
    </>
  );
}

/** Name, username and contact details. */
export function ProfileSheet({ state, copy }: EditSheetProps) {
  const { t, language } = useApp();
  const { form, patch } = state;
  const text = (key: "name" | "username" | "email", label: string, type: "text" | "email" = "text") => (
    <SheetField id={`settings-${key}`} label={label}>
      <Input
        id={`settings-${key}`}
        type={type}
        value={form[key]}
        onChange={(event) => patch({ [key]: event.target.value } as Partial<ProfileForm>)}
        autoComplete={type === "email" ? "email" : undefined}
      />
    </SheetField>
  );

  return (
    <SettingsSheet
      open={state.sheet === "profile"}
      onClose={state.closeSheet}
      title={copy.profileSheetTitle}
      description={copy.profileSheetDesc}
      footer={<SaveFooter state={state} />}
    >
      {text("name", t.name)}
      {text("username", t.settingsUsername)}
      {text("email", t.email, "email")}
      <SheetField id="settings-country" label={t.settingsCountry}>
        <CountrySelect
          id="settings-country"
          value={form.country}
          onChange={(country) => patch({ country: country.name })}
          lang={language}
          placeholder={copy.choose}
          searchPlaceholder={copy.countrySearch}
          emptyLabel={copy.countryNotFound}
        />
      </SheetField>
      {/* Same phone field as sign-up: the dialling code follows the country. */}
      <SheetField id="settings-phone" label={t.settingsPhone}>
        <CountryPhoneInput
          country={form.country}
          phone={form.phoneNumber}
          onCountryChange={(country) => patch({ country: country.name })}
          onPhoneChange={(phoneNumber) => patch({ phoneNumber })}
          countryPlaceholder={t.settingsCountry}
          phonePlaceholder={t.settingsPhone}
        />
      </SheetField>
      <SheetField id="settings-gender" label={t.settingsGender}>
        <Select value={form.gender || undefined} onValueChange={(gender) => patch({ gender })}>
          <SelectTrigger id="settings-gender">
            <SelectValue placeholder={copy.choose} />
          </SelectTrigger>
          <SelectContent>
            {getGenders(t).map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </SheetField>
    </SettingsSheet>
  );
}

/** Spoken, contribution and moderation languages, plus the language of messages. */
export function LanguagesSheet({ state, copy }: EditSheetProps) {
  const { t, user } = useApp();
  const { form, patch } = state;
  return (
    <SettingsSheet
      open={state.sheet === "languages"}
      onClose={state.closeSheet}
      title={copy.languagesTitle}
      description={t.settingsLanguagePreferencesDescription}
      footer={<SaveFooter state={state} />}
    >
      <LanguagePreferencesFields
        role={user?.role}
        value={{
          spokenLanguages: form.spokenLanguages,
          contributionLanguages: form.contributionLanguages,
          moderationLanguages: form.moderationLanguages,
        }}
        copy={t}
        onChange={patch}
      />
      <SheetField id="settings-locale" label={t.preferredCommunicationLanguage} hint={t.preferredCommunicationLanguageDescription}>
        <Select value={form.preferredLocale} onValueChange={(value: "fr" | "en") => patch({ preferredLocale: value })}>
          <SelectTrigger id="settings-locale">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="fr">{t.french}</SelectItem>
            <SelectItem value="en">{t.english}</SelectItem>
          </SelectContent>
        </Select>
      </SheetField>
    </SettingsSheet>
  );
}

/** Current password, then the new one twice. */
export function PasswordSheet({ state, copy }: EditSheetProps) {
  const { t } = useApp();
  const { form, patch } = state;
  const field = (key: "currentPassword" | "newPassword" | "confirmPassword", label: string, autoComplete: string) => (
    <SheetField id={`settings-${key}`} label={label}>
      <Input
        id={`settings-${key}`}
        type="password"
        value={form[key]}
        autoComplete={autoComplete}
        onChange={(event) => patch({ [key]: event.target.value } as Partial<ProfileForm>)}
      />
    </SheetField>
  );

  return (
    <SettingsSheet
      open={state.sheet === "password"}
      onClose={state.closeSheet}
      title={copy.password}
      description={copy.passwordSheetDesc}
      footer={<SaveFooter state={state} />}
    >
      {field("currentPassword", t.settingsCurrentPassword, "current-password")}
      {field("newPassword", t.settingsNewPassword, "new-password")}
      {field("confirmPassword", t.settingsConfirmPassword, "new-password")}
    </SettingsSheet>
  );
}
